import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, X, AlertTriangle, CheckCircle2, Layers, Grid3x3 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SIDES = [
  { key: 'driver', label: 'Driver Side' },
  { key: 'passenger', label: 'Passenger Side' },
  { key: 'both', label: 'Both' },
];

// Live required-credit calculation (display only — backend is authoritative).
export const computeRequiredCredits = (coverageType, packageValue, items, rulesMap) => {
  if (coverageType === 'package') {
    const r = rulesMap[packageValue];
    return { total: r ? (r.capacity_required || 0) : 0, hasReview: false };
  }
  let total = 0;
  let hasReview = false;
  (items || []).forEach((it) => {
    const r = rulesMap[it.area_key];
    if (!r) return;
    if (r.requires_review || r.capacity_required == null) { hasReview = true; return; }
    const qty = it.side === 'both' ? 2 : 1;
    total += (r.capacity_required || 0) * qty;
  });
  return { total, hasReview };
};

export const CoverageSelector = ({
  coverageType, setCoverageType,
  packageValue, setPackageValue,
  items, setItems,
  availability,
}) => {
  const [groups, setGroups] = useState([]);
  const [rulesMap, setRulesMap] = useState({});
  const [pickerValue, setPickerValue] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/coverage-rules`);
        setGroups(res.data.groups || []);
        const map = {};
        (res.data.rules || []).forEach((r) => { map[r.area_key] = r; });
        setRulesMap(map);
      } catch (e) { /* handled by parent toasts */ }
    })();
  }, []);

  const packages = useMemo(() => (groups.find((g) => g.category === 'packages')?.rules) || [], [groups]);
  const individualGroups = useMemo(() => groups.filter((g) => g.category !== 'packages'), [groups]);

  const eligibility = availability?.eligibility || {};
  const remaining = availability?.remaining;

  const { total, hasReview } = computeRequiredCredits(coverageType, packageValue, items, rulesMap);
  const insufficient = availability?.exists && remaining != null && !hasReview && total > remaining && total > 0;
  const rollDisabled = availability?.disabled;

  const addItem = (areaKey) => {
    if (!areaKey) return;
    const rule = rulesMap[areaKey];
    setItems([...(items || []), {
      area_key: areaKey,
      side: rule?.supports_side ? 'driver' : null,
      custom_description: '',
      custom_notes: '',
    }]);
    setPickerValue('');
  };

  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const itemCredits = (it) => {
    const r = rulesMap[it.area_key];
    if (!r || r.requires_review || r.capacity_required == null) return null;
    const qty = it.side === 'both' ? 2 : 1;
    return (r.capacity_required || 0) * qty;
  };

  return (
    <div className="space-y-4" data-testid="coverage-selector">
      {/* Remaining capacity banner */}
      {availability?.exists && (
        <div className={`flex items-center justify-between rounded-lg px-4 py-3 text-sm border ${
          rollDisabled ? 'bg-red-500/10 border-red-500/40 text-red-300'
            : 'bg-black/40 border-amber-500/30 text-amber-100'}`}
          data-testid="roll-remaining-banner">
          <span className="flex items-center gap-2">
            {rollDisabled ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-amber-400" />}
            {rollDisabled ? 'This roll is disabled and cannot be used.'
              : `Roll coverage remaining: ${remaining} of ${availability.total_capacity} credits`}
          </span>
          {!rollDisabled && (
            <span className="font-mono font-bold text-amber-400" data-testid="roll-remaining-value">{remaining}</span>
          )}
        </div>
      )}

      {/* Coverage type toggle */}
      <div>
        <label className="tc-label">Coverage Type *</label>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <button type="button" data-testid="coverage-type-package"
            onClick={() => setCoverageType('package')}
            className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              coverageType === 'package' ? 'bg-amber-500 text-black border-amber-500'
                : 'bg-black/40 text-amber-100 border-amber-500/30 hover:border-amber-500/60'}`}>
            <Layers className="w-4 h-4" /> Package
          </button>
          <button type="button" data-testid="coverage-type-individual"
            onClick={() => setCoverageType('individual')}
            className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              coverageType === 'individual' ? 'bg-amber-500 text-black border-amber-500'
                : 'bg-black/40 text-amber-100 border-amber-500/30 hover:border-amber-500/60'}`}>
            <Grid3x3 className="w-4 h-4" /> Individual Areas
          </button>
        </div>
      </div>

      {/* PACKAGE MODE */}
      {coverageType === 'package' && (
        <div>
          <label className="tc-label">Installation Package *</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
            {packages.map((p) => {
              const disabled = availability?.exists && (eligibility[p.area_key] === false);
              const selected = packageValue === p.area_key;
              return (
                <button key={p.area_key} type="button"
                  data-testid={`package-option-${p.area_key}`}
                  disabled={disabled}
                  onClick={() => setPackageValue(p.area_key)}
                  className={`rounded-lg border px-3 py-2 text-xs font-medium text-left transition-colors ${
                    selected ? 'bg-amber-500 text-black border-amber-500'
                      : disabled ? 'bg-black/20 text-gray-600 border-gray-700 cursor-not-allowed'
                      : 'bg-black/40 text-amber-100 border-amber-500/30 hover:border-amber-500/60'}`}>
                  <div className="font-semibold">{p.name}</div>
                  {disabled && <div className="text-[10px] text-red-400">Unavailable</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* INDIVIDUAL MODE */}
      {coverageType === 'individual' && (
        <div className="space-y-3">
          <label className="tc-label">Installation Coverage *</label>

          {(items || []).map((it, idx) => {
            const rule = rulesMap[it.area_key] || {};
            const credits = itemCredits(it);
            const review = rule.requires_review || rule.capacity_required == null;
            return (
              <div key={idx} className="rounded-lg border border-amber-500/30 bg-black/40 p-3 space-y-2"
                data-testid={`coverage-item-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-amber-100">{rule.name || it.area_key}</span>
                  <div className="flex items-center gap-3">
                    {credits != null && (
                      <span className="text-xs font-mono text-amber-400" data-testid={`coverage-item-credits-${idx}`}>{credits} cr</span>
                    )}
                    {review && <span className="text-xs text-amber-400">Admin review</span>}
                    <button type="button" onClick={() => removeItem(idx)}
                      data-testid={`coverage-item-remove-${idx}`}
                      className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
                  </div>
                </div>

                {rule.supports_side && (
                  <div className="flex gap-2">
                    {SIDES.map((s) => (
                      <button key={s.key} type="button"
                        data-testid={`coverage-item-side-${idx}-${s.key}`}
                        onClick={() => updateItem(idx, { side: s.key })}
                        className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${
                          it.side === s.key ? 'bg-amber-500 text-black border-amber-500'
                            : 'bg-black/30 text-amber-100 border-amber-500/30 hover:border-amber-500/60'}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}

                {review && (
                  <div className="space-y-2">
                    <input className="tc-input tc-focus-ring text-sm"
                      placeholder="Describe the area / part *"
                      value={it.custom_description || ''}
                      data-testid={`coverage-item-desc-${idx}`}
                      onChange={(e) => updateItem(idx, { custom_description: e.target.value })} />
                    <p className="text-[11px] text-amber-400/80">Custom areas require TC Admin review before a certificate is issued.</p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add area picker */}
          <div>
            <select className="tc-select tc-focus-ring" value={pickerValue}
              data-testid="coverage-add-area-select"
              onChange={(e) => addItem(e.target.value)}>
              <option value="">+ Add Installation Area…</option>
              {individualGroups.map((g) => (
                <optgroup key={g.category} label={g.label}>
                  {g.rules.map((r) => {
                    const disabled = availability?.exists && (eligibility[r.area_key] === false);
                    return (
                      <option key={r.area_key} value={r.area_key} disabled={disabled}>
                        {r.name}{r.capacity_required != null ? ` (${r.capacity_required} cr${r.supports_side ? '/side' : ''})` : ' (review)'}{disabled ? ' — unavailable' : ''}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Totals + validation messaging */}
      <div className="flex items-center justify-between rounded-lg bg-black/30 border border-amber-500/20 px-4 py-2">
        <span className="text-sm text-amber-100">Total required coverage</span>
        <span className="font-mono font-bold text-amber-400" data-testid="coverage-total-credits">
          {total}{hasReview ? ' + review' : ''} credits
        </span>
      </div>

      {insufficient && (
        <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/40 px-4 py-3 text-sm text-red-300"
          data-testid="coverage-insufficient-warning">
          <AlertTriangle className="w-4 h-4" />
          This roll does not have enough remaining coverage ({total} needed, {remaining} available). Verify the roll or use another eligible roll.
        </div>
      )}
    </div>
  );
};

export default CoverageSelector;
